import { useEffect, useRef, useState } from 'react';
import { ScheduleCancelReason, ScheduleCancelStatus } from '../../../entities/ScheduleCancel';
import { useCancelScheduleMutation, useCancelScheduleReasonMutation } from '../../../services/scheduleCancel.service';
import { dynamicCustomEventTracking } from '../../../lib/gtm';
import { getPersistedSessionId, useSelectedToken } from '../../../providers/AssistantUIProvider';
import ScheduleCancelModel from './model';

type ScheduleCancelViewModelProps = {
  id: string;
};

type CancelResult = 'pending' | 'success' | 'error';

// cache de módulo: sobrevive à remontagem da mensagem quando o chat reimporta o histórico,
// evitando disparar o cancelamento (e o evento do GTM) mais de uma vez para o mesmo agendamento
const cancelRequests = new Map<string, Promise<void>>();
const sentReasons = new Map<string, ScheduleCancelReason>();

export default function ScheduleCancelViewModel({ id }: ScheduleCancelViewModelProps) {
  const model = ScheduleCancelModel;
  const token = useSelectedToken();

  const [cancelSchedule] = useCancelScheduleMutation();
  const [cancelScheduleReason, { isLoading: isSendingReason, isSuccess: hasSentReason, isError: hasReasonError }] =
    useCancelScheduleReasonMutation();

  const [cancelResult, setCancelResult] = useState<CancelResult>('pending');
  const [selectedReason, setSelectedReason] = useState<ScheduleCancelReason | null>(sentReasons.get(id) ?? null);
  const isMounted = useRef(true);

  const requestCancel = () => {
    let request = cancelRequests.get(id);

    if (!request) {
      const sessionId = getPersistedSessionId();
      if (!sessionId) {
        setCancelResult('error');
        return;
      }

      dynamicCustomEventTracking.send({ dynamicEventName: 'chat_support_schedule_cancel_ag' });
      request = cancelSchedule({ id, chatSessionId: sessionId, token }).unwrap();
      cancelRequests.set(id, request);
      // libera o retry quando a requisição falha
      request.catch(() => cancelRequests.delete(id));
    }

    setCancelResult('pending');
    request
      .then(() => isMounted.current && setCancelResult('success'))
      .catch(() => isMounted.current && setCancelResult('error'));
  };

  useEffect(() => {
    isMounted.current = true;
    requestCancel();
    return () => {
      isMounted.current = false;
    };
  }, [id]);

  const status = model.resolveStatus({
    isCancelling: cancelResult === 'pending',
    isCancelled: cancelResult === 'success',
    hasCancelError: cancelResult === 'error',
    isSendingReason,
    isReasonSent: hasSentReason || sentReasons.has(id)
  });

  const handleSelectReason = (reason: ScheduleCancelReason) => {
    if (!model.canSelectReason(status)) return;
    dynamicCustomEventTracking.send({ dynamicEventName: 'chat_support_schedule_cancel_reason_ag' });
    setSelectedReason(reason);
    cancelScheduleReason({ id, reason, token })
      .unwrap()
      .then(() => sentReasons.set(id, reason))
      .catch(() => undefined);
  };

  return {
    status,
    options: model.REASON_OPTIONS,
    selectedReason,
    handleSelectReason,
    isLoadingCancel: status === ScheduleCancelStatus.CANCELLING,
    hasCancelError: status === ScheduleCancelStatus.ERROR,
    isReasonDisabled: !model.canSelectReason(status),
    isSendingReason: status === ScheduleCancelStatus.SENDING_REASON,
    isReasonSent: status === ScheduleCancelStatus.DONE,
    hasReasonError: hasReasonError && status === ScheduleCancelStatus.CHOOSING_REASON,
    loadingMessage: model.LOADING_MESSAGE,
    reasonPromptMessage: model.REASON_PROMPT_MESSAGE,
    sendingReasonMessage: model.SENDING_REASON_MESSAGE,
    reasonSuccessMessage: model.REASON_SUCCESS_MESSAGE,
    cancelErrorMessage: model.CANCEL_ERROR_MESSAGE,
    reasonErrorMessage: model.REASON_ERROR_MESSAGE,
    retry: requestCancel
  };
}
